import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    WindowsLogo,
    DownloadSimple,
    FileZip,
    WifiSlash,
    Rows,
    ArrowsClockwise,
} from "@phosphor-icons/react";
import { api, API, formatApiErrorDetail } from "../lib/api";

const BACKEND = API.replace(/\/api$/, "");

const POINTS = [
    { icon: Rows, text: "Bulk cards from Excel + photos, front & back" },
    { icon: WifiSlash, text: "Works offline — credits sync automatically" },
    { icon: ArrowsClockwise, text: "Same account & credits as the website" },
];

/**
 * Dashboard card for the Windows desktop app. Only shown to signed-in
 * users; the API returns short-lived signed links, fetched fresh on click
 * so they never expire on an open tab.
 */
export default function DesktopAppCard() {
    const [files, setFiles] = useState(null);
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        api.get("/desktop-app")
            .then((r) => setFiles(r.data.files))
            .catch(() => setFiles({}));
    }, []);

    const download = async (key) => {
        setBusy(key);
        try {
            const { data } = await api.get("/desktop-app");
            const file = data.files?.[key];
            if (!file) throw new Error("Download not available yet — please contact support.");
            window.location.href = `${BACKEND}${file.url}`;
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setTimeout(() => setBusy(null), 1500);
        }
    };

    const setup = files?.setup;
    const zip = files?.zip;

    return (
        <div
            className="relative overflow-hidden mb-10 p-7 text-white shadow-lg"
            style={{ background: "linear-gradient(135deg,#0F766E 0%,#0E7490 50%,#1E3A8A 100%)" }}
            data-testid="desktop-app-card"
        >
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
            <div className="relative flex flex-wrap items-center justify-between gap-6">
                <div className="max-w-xl">
                    <div className="inline-flex items-center gap-1.5 bg-white/15 border border-white/25 px-3 py-1 text-xs font-semibold">
                        <WindowsLogo size={14} weight="fill" /> Windows desktop app
                    </div>
                    <h2 className="font-display font-black text-2xl tracking-tight mt-3">
                        IDCardGen Desktop Studio
                    </h2>
                    <p className="text-white/80 text-sm mt-1">
                        Design and print ID cards on your PC — horizontal or vertical, hundreds at a time.
                    </p>
                    <ul className="mt-4 space-y-2">
                        {POINTS.map(({ icon: Icon, text }) => (
                            <li key={text} className="flex items-center gap-2 text-sm text-white/90">
                                <Icon size={16} weight="bold" className="text-teal-200" /> {text}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="flex flex-col items-stretch gap-2 min-w-[220px]">
                    {files === null ? (
                        <div className="text-sm text-white/70">Checking download…</div>
                    ) : setup ? (
                        <>
                            <button
                                onClick={() => download("setup")}
                                disabled={busy !== null}
                                className="bg-white text-teal-700 hover:bg-teal-50 px-6 py-3 font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-70"
                                data-testid="desktop-download-btn"
                            >
                                <DownloadSimple size={18} weight="bold" />
                                {busy === "setup" ? "Starting download…" : "Download for Windows"}
                            </button>
                            <div className="text-center text-xs text-white/70">
                                Setup · {setup.size_mb} MB · Windows 10/11 · no admin needed
                            </div>
                            {zip && (
                                <button
                                    onClick={() => download("zip")}
                                    disabled={busy !== null}
                                    className="text-xs text-white/80 hover:text-white underline flex items-center justify-center gap-1"
                                >
                                    <FileZip size={14} /> Portable zip ({zip.size_mb} MB)
                                </button>
                            )}
                        </>
                    ) : (
                        <div className="bg-white/15 px-5 py-3 text-sm text-center">
                            Download coming soon — contact support.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
